import React from 'react';
import {
  Box,
  Paper,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import type { VMSSite } from '../../types/canvision.types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EventStatusFilter = 'all' | 'new' | 'acknowledged' | 'dismissed' | 'promoted';

export interface EventsFilters {
  siteId: number | null;
  type: string;
  severity: string;
  status: EventStatusFilter;
}

export interface EventsControlBarProps {
  /** Available sites for the Site autocomplete */
  sites: VMSSite[];
  /** Current filter values */
  filters: EventsFilters;
  /** Called when any filter value changes */
  onFiltersChange: (filters: EventsFilters) => void;
  /** Number of currently selected event rows */
  selectedCount: number;
  /** Bulk action: acknowledge selected events */
  onBulkAcknowledge: () => void;
  /** Bulk action: dismiss selected events */
  onBulkDismiss: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'person', label: 'Person' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'motion', label: 'Motion' },
  { value: 'face', label: 'Face' },
  { value: 'animal', label: 'Animal' },
  { value: 'intrusion', label: 'Intrusion' },
  { value: 'loitering', label: 'Loitering' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'All Severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const STATUS_CHIPS: {
  value: EventStatusFilter;
  label: string;
  activeBg: string;
  activeText: string;
}[] = [
  { value: 'all', label: 'All', activeBg: 'primary.main', activeText: 'common.white' },
  { value: 'new', label: 'New', activeBg: 'primary.main', activeText: 'common.white' },
  { value: 'acknowledged', label: 'Acknowledged', activeBg: 'grey.500', activeText: 'common.white' },
  { value: 'dismissed', label: 'Dismissed', activeBg: 'success.main', activeText: 'common.white' },
  { value: 'promoted', label: 'Promoted', activeBg: 'info.main', activeText: 'common.white' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function EventsControlBar({
  sites,
  filters,
  onFiltersChange,
  selectedCount,
  onBulkAcknowledge,
  onBulkDismiss,
}: EventsControlBarProps) {
  const hasBulkSelection = selectedCount > 0;

  // Derive the currently-selected site object for the Autocomplete
  const selectedSite = sites.find((s) => s.id === filters.siteId) ?? null;

  // Helpers to update a single filter key
  const updateFilter = <K extends keyof EventsFilters>(key: K, value: EventsFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        px: 2,
        py: 1.5,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      {/* ---- Left: Filters ------------------------------------------------ */}

      {/* Site Autocomplete */}
      <Autocomplete
        size="small"
        sx={{ minWidth: 180 }}
        options={sites}
        getOptionLabel={(site) => site.display_name || site.name}
        value={selectedSite}
        onChange={(_e, newSite) => updateFilter('siteId', newSite?.id ?? null)}
        renderInput={(params) => <TextField {...params} label="Site" />}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />

      {/* Type Select */}
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={filters.type}
          label="Type"
          onChange={(e) => updateFilter('type', e.target.value)}
        >
          {EVENT_TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Severity Select */}
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Severity</InputLabel>
        <Select
          value={filters.severity}
          label="Severity"
          onChange={(e) => updateFilter('severity', e.target.value)}
        >
          {SEVERITY_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Status filter chips */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {STATUS_CHIPS.map((chip) => {
          const isActive = filters.status === chip.value;
          return (
            <Chip
              key={chip.value}
              label={chip.label}
              size="small"
              variant={isActive ? 'filled' : 'outlined'}
              onClick={() => updateFilter('status', chip.value)}
              clickable
              sx={
                isActive
                  ? {
                      bgcolor: chip.activeBg,
                      color: chip.activeText,
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: chip.activeBg, opacity: 0.9 },
                    }
                  : undefined
              }
            />
          );
        })}
      </Box>

      {/* ---- Spacer ------------------------------------------------------- */}
      <Box sx={{ flex: 1 }} />

      {/* ---- Right: Bulk actions ------------------------------------------ */}

      {/* Selected counter chip */}
      {hasBulkSelection && (
        <Chip
          label={
            <Typography variant="caption" fontWeight="bold">
              {selectedCount} selected
            </Typography>
          }
          size="small"
          color="primary"
          variant="outlined"
        />
      )}

      <Button
        size="small"
        variant="outlined"
        startIcon={<VisibilityIcon />}
        disabled={!hasBulkSelection}
        onClick={onBulkAcknowledge}
      >
        Acknowledge
      </Button>

      <Button
        size="small"
        variant="outlined"
        startIcon={<BlockIcon />}
        disabled={!hasBulkSelection}
        onClick={onBulkDismiss}
      >
        Dismiss
      </Button>
    </Paper>
  );
}

export default EventsControlBar;
